import React from "react";
import { Card, Typography, Space } from "antd";
import {
  ClockCircleOutlined,
  UserOutlined,
  PaperClipOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import { formatDateByLanguage } from "utils/formatDateByLanguage";

const { Title, Text, Paragraph } = Typography;

interface OriginalReportSectionProps {
  reportData: any;
}

const OriginalReportSection: React.FC<OriginalReportSectionProps> = ({
  reportData,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Submission Details */}
      <Card className="bg-white-A700 shadow-md">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
          <Title level={4} className="!mb-0 !text-lg md:!text-xl">
            {t("submissionDetails")}
          </Title>
          <div className="flex items-center gap-2">
            <UserOutlined className="text-gray-500" />
            <Text type="secondary" className="text-sm md:text-base">
              {reportData.receiptMethod === "web"
                ? t("webSubmission")
                : t("anonymousSubmission")}
            </Text>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <ClockCircleOutlined className="text-gray-500" />
          <Text type="secondary" className="text-sm md:text-base">
            {t("submittedOn")}{" "}
            {reportData.createdAt
              ? formatDateByLanguage(reportData.createdAt)
              : ""}
          </Text>
        </div>
      </Card>

      {/* Original Report */}
      <Card className="bg-white-A700 shadow-md">
        <Title level={4} className="!mb-0 !text-lg md:!text-xl">
          {t("originalReport")}
        </Title>
        <div className="bg-gray-50 rounded-lg p-3 md:p-4 mt-3">
          <Paragraph className="!mb-0 text-gray-700 text-sm md:text-base whitespace-pre-line">
            {reportData.reportText}
          </Paragraph>
        </div>
      </Card>

      {/* Attachments */}
      <Card className="bg-white-A700 shadow-md">
        <div className="flex items-center gap-2 mb-4">
          <Title level={4} className="!mb-0 !text-lg md:!text-xl">
            {t("attachments")}
          </Title>
        </div>

        {reportData.documents && reportData.documents.length > 0 ? (
          <div className="space-y-2">
            {reportData.documents.map((doc: any) => (
              <a
                href={doc.url}
                target="_blank"
                key={doc.uuid}
                className="flex items-center gap-2"
                rel="noreferrer"
              >
                <PaperClipOutlined />
                {doc.name}
              </a>
            ))}
          </div>
        ) : (
          <Text type="secondary" italic className="text-sm md:text-base">
            {t("noAttachments")}
          </Text>
        )}
      </Card>

      {/* Follow-up Questions & Answers */}
      <Card className="bg-white-A700 shadow-md">
        <div className="flex items-center gap-2 mb-4">
          <Title level={4} className="!mb-0 !text-lg md:!text-xl">
            {t("followUpQuestionsAnswers")}
          </Title>
        </div>

        {reportData.answers && reportData.answers.length > 0 ? (
          <div className="space-y-8">
            {reportData.answers.map((answer: any) => (
              <div key={answer.uuid} className="flex items-start gap-2">
                <MessageOutlined className="mt-1" />
                <div>
                  <Text strong>{answer.question}</Text>
                  <Text type="secondary" className="block">
                    {answer.answer}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Info size={16} color="var(--gray_500)" />
            <Text type="secondary" italic className="text-sm md:text-base">
              {t("noFollowUpQuestions")}
            </Text>
          </div>
        )}
      </Card>
    </div>
  );
};

export default OriginalReportSection;
